// Plan Summary Component
// Displays comprehensive plan statistics similar to Plan Detail page

import { Card, CardContent } from '@/components/ui/card';
import { Mic2, Music, Users, DollarSign } from 'lucide-react';
import { formatPrice } from '@/lib/package-config';
import { getAttendeesByCareerStage } from '@shared/career-stage-attendance';

interface PlanItem {
  id: string;
  careerStage: string;
  numberOfShows: number;
  numberOfArtists: number;
  showsPerArtist: number;
  artistsWithBaseShows?: number;
  artistsWithExtraShow?: number;
  packageTier: 'set-core' | 'set-audio' | 'set-video';
  pricePerShow: number;
  totalPrice: number;
}

interface PlanSummaryProps {
  planItems: PlanItem[];
}

export function PlanSummary({ planItems }: PlanSummaryProps) {
  // Show empty state if no plan items
  if (planItems.length === 0) {
    return (
      <Card className="border-pink-200/50 overflow-hidden shadow-lg h-full flex flex-col">
        {/* Hero Price Section with Gradient */}
        <div className="bg-gradient-to-br from-[#FA017C] to-[#0240FB] p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent animate-pulse opacity-50"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-sm font-medium opacity-90">Total Investment</p>
            </div>
            <p className="text-5xl font-bold mb-3">
              {formatPrice(0)}
            </p>
            <div className="pt-3 border-t border-white/20">
              <p className="text-lg font-medium">
                {formatPrice(0)} <span className="text-sm opacity-80">avg per show</span>
              </p>
            </div>
          </div>
        </div>

        {/* Stat Grid */}
        <CardContent className="p-6 space-y-3 flex-1 flex flex-col justify-center">
          {/* Row 1: Shows, Artists */}
          <div className="grid grid-cols-2 gap-3">
            {/* Shows Card */}
            <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-blue-500 shadow-md transition-all duration-300 overflow-hidden">
              <div className="relative z-10 text-center">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                  <Mic2 className="h-5 w-5 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-blue-900">0</p>
                <p className="text-xs text-blue-600 font-medium">Shows</p>
              </div>
            </div>

            {/* Artists Card */}
            <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-pink-500 shadow-md transition-all duration-300 overflow-hidden">
              <div className="relative z-10 text-center">
                <div className="w-9 h-9 bg-pink-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                  <Music className="h-5 w-5 text-pink-600" />
                </div>
                <p className="text-2xl font-bold text-pink-900">0</p>
                <p className="text-xs text-pink-600 font-medium">Artists</p>
              </div>
            </div>
          </div>

          {/* Row 2: Potential Live Attendees Card (Full Width) */}
          <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-l-4 border-green-500 shadow-md transition-all duration-300 overflow-hidden">
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-900">0</p>
                  <p className="text-xs text-green-600 font-medium whitespace-nowrap">Live Attendees</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-900">0</p>
                <p className="text-xs text-green-600 font-medium whitespace-nowrap">avg per show</p>
              </div>
            </div>
          </div>

          {/* Note about adding shows */}
          <div className="text-xs text-gray-500 text-center pt-2">
            <p>Add shows to your plan to see summary statistics</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate summary stats
  const totalInvestment = planItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const totalShows = planItems.reduce((sum, item) => sum + item.numberOfShows, 0);
  const avgPerShow = Math.round(totalInvestment / totalShows);

  // Calculate actual artist count (sum of all artists across all items)
  const artistCount = planItems.reduce((sum, item) => sum + item.numberOfArtists, 0);

  // Calculate live attendees using career stage attendance data
  const totalAttendees = planItems.reduce((sum, item) => {
    const avgAttendeesForStage = getAttendeesByCareerStage(item.careerStage);
    return sum + (avgAttendeesForStage * item.numberOfShows);
  }, 0);
  const avgAttendeesPerShow = Math.round(totalAttendees / totalShows);

  return (
    <Card className="border-pink-200/50 overflow-hidden shadow-lg h-full flex flex-col">
      {/* Hero Price Section with Gradient */}
      <div className="bg-gradient-to-br from-[#FA017C] to-[#0240FB] p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent animate-pulse opacity-50"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm font-medium opacity-90">Total Investment</p>
          </div>
          <p className="text-5xl font-bold mb-3">
            {formatPrice(totalInvestment)}
          </p>
          <div className="pt-3 border-t border-white/20">
            <p className="text-lg font-medium">
              {formatPrice(avgPerShow)} <span className="text-sm opacity-80">avg per show</span>
            </p>
          </div>
        </div>
      </div>

      {/* Stat Grid */}
      <CardContent className="p-6 space-y-3 flex-1 flex flex-col justify-center">
        {/* Row 1: Shows, Artists */}
        <div className="grid grid-cols-2 gap-3">
          {/* Shows Card */}
          <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-blue-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10 text-center">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                <Mic2 className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-900">{totalShows}</p>
              <p className="text-xs text-blue-600 font-medium">Shows</p>
            </div>
          </div>

          {/* Artists Card */}
          <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-pink-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10 text-center">
              <div className="w-9 h-9 bg-pink-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-pink-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                <Music className="h-5 w-5 text-pink-600" />
              </div>
              <p className="text-2xl font-bold text-pink-900">{artistCount}</p>
              <p className="text-xs text-pink-600 font-medium">
                {artistCount === 1 ? 'Artist' : 'Artists'}
              </p>
            </div>
          </div>
        </div>

        {/* Row 2: Potential Live Attendees Card (Full Width) */}
        <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-l-4 border-green-500 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 group-hover:scale-110 transition-all duration-300">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-900">{totalAttendees.toLocaleString()}</p>
                <p className="text-xs text-green-600 font-medium whitespace-nowrap">Live Attendees</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-green-900">{avgAttendeesPerShow.toLocaleString()}</p>
              <p className="text-xs text-green-600 font-medium whitespace-nowrap">avg per show</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
